import { useEffect } from 'react';
import { Route, Switch, useLocation } from 'wouter';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useLenis, getLenis } from '@/lib/useLenis';
import { Nav } from '@/components/layout/Nav';
import { Footer } from '@/components/layout/Footer';
import { StructuredData } from '@/components/seo/StructuredData';
import Home from '@/pages/Home';
import Services from '@/pages/Services';
import ServiceDetail from '@/pages/ServiceDetail';
import Pricing from '@/pages/Pricing';
import About from '@/pages/About';
import Portfolio from '@/pages/Portfolio';
import ProjectDetail from '@/pages/ProjectDetail';
import Careers from '@/pages/Careers';
import Contact from '@/pages/Contact';
import Privacy from '@/pages/Privacy';
import NotFound from '@/pages/NotFound';

/**
 * Resets scroll on navigation and refreshes ScrollTrigger for the new page's
 * measurements. Without the refresh, triggers keep the previous page's offsets
 * and fire at the wrong positions.
 */
function ScrollToTop() {
  const [location] = useLocation();

  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      // Let the target render before scrolling to it.
      requestAnimationFrame(() => {
        const el = document.querySelector(hash);
        if (el) {
          const lenis = getLenis();
          if (lenis) lenis.scrollTo(el as HTMLElement, { offset: -96 });
          else el.scrollIntoView();
        }
      });
    } else {
      const lenis = getLenis();
      if (lenis) lenis.scrollTo(0, { immediate: true });
      else window.scrollTo(0, 0);
    }
    ScrollTrigger.refresh();
  }, [location]);

  return null;
}

export default function App() {
  useLenis();

  return (
    <>
      <StructuredData />
      <Nav />
      <ScrollToTop />
      <main id="main">
        <Switch>
          <Route path="/" component={Home} />
          <Route path="/services" component={Services} />
          <Route path="/services/:slug" component={ServiceDetail} />
          <Route path="/pricing" component={Pricing} />
          <Route path="/about" component={About} />
          <Route path="/portfolio" component={Portfolio} />
          <Route path="/portfolio/:slug" component={ProjectDetail} />
          <Route path="/careers" component={Careers} />
          <Route path="/contact" component={Contact} />
          <Route path="/privacy" component={Privacy} />
          <Route component={NotFound} />
        </Switch>
      </main>
      <Footer />
    </>
  );
}
